import {
  type ComponentPropsWithoutRef,
  type ElementType,
  type ForwardedRef,
  type JSX,
} from 'react';

/* eslint-disable-next-line no-restricted-imports */
import RootMenuItem, { type MenuItemTypeMap as RootMenuItemTypeMap } from '@mui/material/MenuItem';
import { type BaseProps, type OverridableTypeMap } from '@mui/material/OverridableComponent';

import { type ListItemComponent, ListItemDefaultComponent, type ListModelHref } from '../types';

type MenuItemContainerTypeMap<E extends ElementType> = RootMenuItemTypeMap<{ component?: E }, E>;

/**
 * Represents the root MUI props for the {@link MenuItemContainer} component.
 *
 * This type must be generated by distributing over the union {@link T}, which is a type map that
 * already distributes over the union {@link E}.
 *
 * This type is analogous to Material UI's {@link OverrideProps} type, with the one exception being
 * the omission of the types from the {@link BaseProps<T>} shown below.  This omission has to be
 * performed on the {@link BaseProps<T>}, instead of on the overall resulting type
 * {@link MenuItemContainer}, due to Material UI internal type definitions related to the types
 * used below.
 *
 * @see https://mui.com/material-ui/guides/composition/#generic for more information related to the
 * extension of generically typed Material UI components.
 */
type RootMenuItemContainerProps<
  E extends ListItemComponent,
  T extends OverridableTypeMap,
> = T extends OverridableTypeMap
  ? Omit<BaseProps<T>, 'disabled' | 'href'> &
      Omit<ComponentPropsWithoutRef<E>, 'href' | 'title' | keyof BaseProps<T>>
  : never;

export type MenuItemContainerPartialProps<E extends ListItemComponent> = {
  readonly href?: ListModelHref<E>;
  readonly isDisabled?: boolean;
};

/**
 * Represents the props for the {@link MenuItemContainer} component, which are a combination of
 * internally defined props and props that are extended directly from MUI,
 * {@link RootMenuItemContainerProps}.
 */
export type MenuItemContainerProps<
  E extends ListItemComponent = ListItemDefaultComponent,
  P = MenuItemContainerPartialProps<E>,
> = P & RootMenuItemContainerProps<E, MenuItemContainerTypeMap<E>>;

export type MenuItemContainerComponentType = <
  E extends ListItemComponent = ListItemDefaultComponent,
>(
  props: { readonly ref?: ForwardedRef<HTMLLIElement> } & MenuItemContainerProps<E>,
) => JSX.Element;

export const MenuItemContainer: MenuItemContainerComponentType = <
  C extends ListItemComponent = ListItemDefaultComponent,
>({
  isDisabled,
  ref,
  ...props
}: { readonly ref?: ForwardedRef<HTMLLIElement> } & MenuItemContainerProps<C>) => (
  <RootMenuItem component={ListItemDefaultComponent} {...props} disabled={isDisabled} ref={ref} />
);
